import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { ACCESS_LEVEL_COPY } from '../constants';
import type { AccessLevel, ConsentAccessLevelInfo } from '../types';

interface ConsentAccessLevelsProps {
  levels: ConsentAccessLevelInfo[];
  selectedLevel: AccessLevel;
  disabled: boolean;
  onSelect: (level: AccessLevel) => void;
}

export function ConsentAccessLevels({
  levels,
  selectedLevel,
  disabled,
  onSelect,
}: ConsentAccessLevelsProps) {
  return (
    <div className='flex flex-col gap-2 w-[352px] max-w-full'>
      <Text
        size={TextSizes.XS}
        weight={FontWeights.MEDIUM}
        className='leading-4 text-secondary'
      >
        Access level
      </Text>
      {levels
        .filter(level => level.level in ACCESS_LEVEL_COPY)
        .map(level => {
          const copy = ACCESS_LEVEL_COPY[level.level];
          const selected = level.level === selectedLevel;

          return (
            <button
              type='button'
              key={level.level}
              disabled={disabled}
              onClick={() => onSelect(level.level)}
              data-testid={`consent-access-level-${level.level}`}
              aria-pressed={selected}
              className={cn(
                'flex items-start gap-3 p-3 text-left rounded-xl border bg-primary disabled:opacity-60',
                selected
                  ? 'border-[var(--color-text-primary)]'
                  : 'border-secondary'
              )}
            >
              <span
                className={cn(
                  'flex-shrink-0 mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center',
                  selected
                    ? 'border-[var(--color-text-primary)]'
                    : 'border-secondary'
                )}
              >
                {selected && (
                  <span className='w-2 h-2 rounded-full bg-[var(--color-text-primary)]' />
                )}
              </span>
              <span className='flex flex-col'>
                <Text
                  size={TextSizes.SM}
                  weight={FontWeights.MEDIUM}
                  className='leading-5 text-primary'
                >
                  {copy.label}
                </Text>
                <Text
                  size={TextSizes.XS}
                  weight={FontWeights.REGULAR}
                  className='leading-[18px] text-secondary mt-0.5'
                >
                  {copy.description}
                  {level.already_granted && ' (already allowed)'}
                </Text>
              </span>
            </button>
          );
        })}
    </div>
  );
}
